/**
 * Template Rendering Core
 * Handles rendering of templates with placeholder replacement
 */

import { TemplateRenderContext, ExportTemplate, FileTemplate, RenderedTemplate } from '../../../types/exportTemplates';
import { applyNamingConvention } from './naming';
import { formatValue } from './formatting';

/**
 * Parse and render a template with context
 */
export function renderTemplate(
  template: string, 
  context: TemplateRenderContext,
  exportTemplate: ExportTemplate
): string {
  let output = template;
  
  const conventions = exportTemplate.namingConventions;
  const platform = exportTemplate.platform;
  
  // Apply naming conventions to context values
  const templateName = applyNamingConvention(context.templateName, conventions.templateName);
  const itemName = context.itemName 
    ? applyNamingConvention(context.itemName, conventions.instanceName)
    : '';
  const fileName = context.itemName
    ? applyNamingConvention(context.itemName, conventions.fileName)
    : applyNamingConvention(context.templateName, conventions.fileName);
  
  // Simple placeholder replacements
  output = output.replace(/\{\{TemplateName\}\}/g, templateName);
  output = output.replace(/\{\{ItemName\}\}/g, itemName);
  output = output.replace(/\{\{FileName\}\}/g, fileName);
  output = output.replace(/\{\{instanceName\}\}/g, itemName);
  
  // Handle {{#each fields}} ... {{/each}} blocks
  const eachFieldsRegex = /\{\{#each fields\}\}([\s\S]*?)\{\{\/each\}\}/g;
  output = output.replace(eachFieldsRegex, (match, fieldTemplate) => {
    return context.fields.map(field => {
      let fieldOutput = fieldTemplate;
      
      // Check for field name override
      let fieldName = applyNamingConvention(field.label, conventions.fieldName);
      if (exportTemplate.fieldNameOverrides && context.metadata?.blueprintId) {
        const blueprintOverrides = exportTemplate.fieldNameOverrides[context.metadata.blueprintId];
        if (blueprintOverrides && blueprintOverrides[field.label]) {
          const override = blueprintOverrides[field.label];
          if (override.useExportName && override.exportName) {
            fieldName = override.exportName;
          }
        }
      }
      
      const fieldType = field.mappedType;
      const fieldValue = field.value !== undefined 
        ? formatValue(field.value, field.mappedType, platform)
        : field.defaultValue;
      const optional = !field.isRequired;
      const comment = field.comment || field.label;
      
      // Replace field-specific placeholders
      fieldOutput = fieldOutput.replace(/\{\{fieldName\}\}/g, fieldName);
      fieldOutput = fieldOutput.replace(/\{\{fieldType\}\}/g, fieldType);
      fieldOutput = fieldOutput.replace(/\{\{fieldValue\}\}/g, fieldValue);
      fieldOutput = fieldOutput.replace(/\{\{comment\}\}/g, comment);
      
      // Handle {{#if optional}} ... {{/if}} conditionals
      const ifOptionalRegex = /\{\{#if optional\}\}([\s\S]*?)\{\{\/if\}\}/g;
      fieldOutput = fieldOutput.replace(ifOptionalRegex, (_, content) => {
        return optional ? content : '';
      });
      
      // Handle {{#if required}} ... {{/if}} conditionals
      const ifRequiredRegex = /\{\{#if required\}\}([\s\S]*?)\{\{\/if\}\}/g;
      fieldOutput = fieldOutput.replace(ifRequiredRegex, (_, content) => {
        return !optional ? content : '';
      });
      
      return fieldOutput;
    }).join('');
  });
  
  // Handle {{functions}} placeholder - inject applicable functions
  if (exportTemplate.functions && exportTemplate.functions.length > 0) {
    const applicableFunctions = exportTemplate.functions.filter(func => {
      if (func.appliesTo === 'all') return true;
      if (Array.isArray(func.appliesTo) && context.metadata?.blueprintId) {
        return func.appliesTo.includes(context.metadata.blueprintId);
      }
      return false;
    });
    
    console.log('🔧 Function injection:', {
      totalFunctions: exportTemplate.functions.length,
      blueprintId: context.metadata?.blueprintId,
      applicableFunctions: applicableFunctions.length,
      functionNames: applicableFunctions.map(f => f.name),
      allFunctions: exportTemplate.functions.map(f => ({
        name: f.name,
        appliesTo: f.appliesTo,
        matches: f.appliesTo === 'all' || (Array.isArray(f.appliesTo) && f.appliesTo.includes(context.metadata?.blueprintId || ''))
      }))
    });
    
    if (applicableFunctions.length > 0) {
      const functionsCode = applicableFunctions.map(func => {
        // Render function content with template placeholders
        let functionCode = func.content;
        
        console.log('🎯 Processing function:', {
          functionName: func.name,
          originalCode: functionCode.substring(0, 150) + '...',
          hasVariables: !!func.variables?.length,
          appliesTo: func.appliesTo
        });
        
        // Replace common template placeholders
        functionCode = functionCode.replace(/\{\{TemplateName\}\}/g, templateName);
        functionCode = functionCode.replace(/\{\{ItemName\}\}/g, itemName);
        functionCode = functionCode.replace(/\{\{instanceName\}\}/g, itemName);
        functionCode = functionCode.replace(/\{\{itemName\}\}/g, context.itemName || '');
        functionCode = functionCode.replace(/\{\{itemId\}\}/g, context.itemId || '');
        functionCode = functionCode.replace(/\{\{blueprintId\}\}/g, context.metadata?.blueprintId || '');
        
        // Handle template-specific field variables
        if (func.appliesTo !== 'all' && func.variables && context.metadata?.blueprintId) {
          const templateVars = func.variables.find(v => v.templateId === context.metadata?.blueprintId);
          
          console.log('🔍 Variable replacement check:', {
            funcAppliesTo: func.appliesTo,
            hasVariables: !!func.variables,
            templateVarsFound: !!templateVars,
            blueprintId: context.metadata?.blueprintId,
            allVariables: func.variables,
            selectedFieldNames: templateVars?.fieldNames || [],
            availableFields: context.fields.map(f => ({ name: f.name, label: f.label, value: f.value }))
          });
          
          if (templateVars && templateVars.fieldNames.length > 0) {
            // Replace field variables with their values from context.fields
            templateVars.fieldNames.forEach(fieldName => {
              const field = context.fields.find(f => 
                f.name === fieldName || f.label === fieldName || f.originalName === fieldName
              );
              
              console.log(`  📌 Replacing {{${fieldName}}}:`, {
                fieldFound: !!field,
                fieldValue: field?.value,
                fieldLabel: field?.label
              });
              
              if (field) {
                const fieldNameInCode = applyNamingConvention(fieldName, conventions.fieldName);
                const fieldValue = field.value !== undefined 
                  ? formatValue(field.value, field.mappedType, platform)
                  : field.defaultValue;
                
                console.log(`    ✏️ Replacement:`, {
                  originalName: fieldName,
                  conventionName: fieldNameInCode,
                  value: fieldValue
                });
                
                // Replace {{fieldName}} with the actual value
                const regex = new RegExp(`\\{\\{${fieldName}\\}\\}`, 'g');
                functionCode = functionCode.replace(regex, fieldValue);
                
                // Also replace with the naming convention version
                const regexNamingConvention = new RegExp(`\\{\\{${fieldNameInCode}\\}\\}`, 'g');
                functionCode = functionCode.replace(regexNamingConvention, fieldValue);
              }
            });
          }
        }
        
        console.log('✅ Function code after replacements:', functionCode);
        
        // Handle common variables for 'all' templates
        if (func.appliesTo === 'all' && func.commonVariables) {
          // Common variables are already replaced above (TemplateName, instanceName, etc.)
          // This section is for any additional common variables that might be needed
        }
        
        return functionCode;
      }).join('\n\n');
      
      console.log('💉 Injecting functions into output:', {
        functionsCodeLength: functionsCode.length,
        hasPlaceholder: output.includes('{{functions}}'),
        outputBeforeLength: output.length
      });
      
      output = output.replace(/\{\{functions\}\}/g, functionsCode);
      
      console.log('✨ After injection:', {
        outputAfterLength: output.length,
        placeholderRemaining: output.includes('{{functions}}')
      });
    } else {
      // Remove placeholder if no functions apply
      console.log('⚠️ No applicable functions, removing placeholder');
      output = output.replace(/\{\{functions\}\}/g, '');
    }
  } else {
    // Remove placeholder if no functions defined
    console.log('⚠️ No functions defined on template, removing placeholder');
    output = output.replace(/\{\{functions\}\}/g, '');
  }
  
  // Clean up extra blank lines (more than 2 consecutive)
  output = output.replace(/\n{3,}/g, '\n\n');
  
  return output;
}

/**
 * Render a file template
 */
export function renderFileTemplate(
  fileTemplate: FileTemplate,
  context: TemplateRenderContext,
  exportTemplate: ExportTemplate
): { filename: string; content: string; location: string } | null {
  if (!fileTemplate.enabled) {
    return null;
  }
  
  // Render filename
  const conventions = exportTemplate.namingConventions;
  const templateName = applyNamingConvention(context.templateName, conventions.templateName);
  const itemName = context.itemName 
    ? applyNamingConvention(context.itemName, conventions.instanceName)
    : '';
  const fileName = context.itemName
    ? applyNamingConvention(context.itemName, conventions.fileName)
    : applyNamingConvention(context.templateName, conventions.fileName);
  
  let filename = fileTemplate.filename;
  filename = filename.replace(/\{\{TemplateName\}\}/g, templateName);
  filename = filename.replace(/\{\{ItemName\}\}/g, itemName);
  filename = filename.replace(/\{\{FileName\}\}/g, fileName);
  
  // Render content
  const content = renderTemplate(fileTemplate.content, context, exportTemplate);
  
  return {
    filename,
    content,
    location: fileTemplate.location
  };
}

/**
 * Render both type and instance files for an export template
 */
export function renderExportTemplate(
  context: TemplateRenderContext,
  exportTemplate: ExportTemplate
): RenderedTemplate {
  const result: RenderedTemplate = {};
  
  // Render type file
  const typeFile = renderFileTemplate(exportTemplate.typeFile, context, exportTemplate);
  if (typeFile) {
    result.typeFile = typeFile;
  }
  
  // Render instance file (only if we have item data)
  if (context.itemName) {
    const instanceFile = renderFileTemplate(exportTemplate.instanceFile, context, exportTemplate);
    if (instanceFile) {
      result.instanceFile = instanceFile;
    }
  }
  
  return result;
}
